/**
 * StorageUIController - Handles storage UI interactions (Save/Open modals)
 * 
 * Manages the Save Storage modal (save patches), Open Storage modal (open/import patches),
 * and related operations like filtering, listing, and deleting patches.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { closeFileMenu } from './MenuController.js';
import { escapeHtml, formatTimestamp } from '../utils/index.js';

// Module state
let storageService = null;
let loadAndApplyPatchFn = null;
let setCurrentPatchNameFn = null;
let recentPatchesService = null;
let invalidatePatchesCacheFn = null;
let autosaveFn = null;
let showConfirmModalFn = null;
let getInstanceCountFn = null;
let getConnectionCountFn = null;
let getCurrentPatchNameFn = null;

// UI state
let openSearchFilter = '';

/**
 * Initialize the StorageUIController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.storageService - Storage service instance
 * @param {Function} deps.loadAndApplyPatch - Callback to load a patch
 * @param {Function} deps.setCurrentPatchName - Callback to set current patch name
 * @param {Object} deps.recentPatchesService - Recent patches service
 * @param {Function} deps.invalidatePatchesCache - Callback to invalidate patches cache
 * @param {Function} deps.autosave - Callback to trigger autosave
 * @param {Function} deps.showConfirmModal - Function to show confirmation modal
 * @param {Function} deps.getInstanceCount - Function to get instance count
 * @param {Function} deps.getConnectionCount - Function to get connection count
 * @param {Function} deps.getCurrentPatchName - Function to get current patch name
 * @returns {Object} Public methods for onclick handlers
 */
export function initStorageUIController(deps) {
    storageService = deps.storageService;
    loadAndApplyPatchFn = deps.loadAndApplyPatch;
    setCurrentPatchNameFn = deps.setCurrentPatchName;
    recentPatchesService = deps.recentPatchesService;
    invalidatePatchesCacheFn = deps.invalidatePatchesCache;
    autosaveFn = deps.autosave;
    showConfirmModalFn = deps.showConfirmModal;
    getInstanceCountFn = deps.getInstanceCount;
    getConnectionCountFn = deps.getConnectionCount;
    getCurrentPatchNameFn = deps.getCurrentPatchName;
    
    // Return public methods for window/onclick exposure
    return {
        saveToStorage,
        renderSaveExistingList,
        selectPatchForSave,
        closeSaveStorageModal,
        confirmSaveToStorage,
        openFromStorage,
        importFromStorage,
        showOpenStorageModal,
        filterOpenPatchList,
        renderOpenPatchList,
        openPatchFromList,
        deletePatchFromList,
        closeOpenStorageModal
    };
}

// ============= SAVE STORAGE MODAL =============

/**
 * Open the Save Storage modal
 */
function saveToStorage() {
    closeFileMenu();
    const modal = document.getElementById(MODAL_IDS.SAVE_STORAGE);
    const nameInput = document.getElementById('save-patch-name');
    
    const currentPatchName = getCurrentPatchNameFn();
    
    // Pre-fill with current patch name (unless it's Untitled)
    nameInput.value = currentPatchName === 'Untitled Patch' ? '' : currentPatchName;
    
    // Render appropriate patch list
    renderSaveExistingList();
    
    modal.classList.add('active');
    nameInput.focus();
}

/**
 * Render existing patches list for save modal (for overwrite selection)
 */
async function renderSaveExistingList() {
    const list = document.getElementById('save-storage-list');
    const syncing = document.getElementById('save-syncing');
    
    syncing.classList.add('active');
    list.innerHTML = '';
    
    const patches = await storageService.listPatches();
    
    syncing.classList.remove('active');
    
    if (patches.length === 0) {
        list.innerHTML = '<div class="storage-empty">No saved patches yet</div>';
    } else {
        list.innerHTML = patches.map(p => `
            <div class="storage-patch-item" onclick="selectPatchForSave('${escapeHtml(p.name)}')">
                <div class="storage-patch-info">
                    <div class="storage-patch-name">${escapeHtml(p.name)}</div>
                    <div class="storage-patch-meta">
                        <span>${p.deviceCount} devices</span>
                        <span>${p.connectionCount} connections</span>
                        <span>${formatTimestamp(p.timestamp)}</span>
                    </div>
                </div>
            </div>
        `).join('');
    }
}

/**
 * Select a patch name for save (fills in the name input)
 * @param {string} name - Patch name to select
 */
function selectPatchForSave(name) {
    document.getElementById('save-patch-name').value = name;
}

/**
 * Close the Save Storage modal
 */
function closeSaveStorageModal() {
    document.getElementById(MODAL_IDS.SAVE_STORAGE).classList.remove('active');
    document.getElementById('save-syncing').classList.remove('active');
}

/**
 * Confirm and save the patch to storage
 */
async function confirmSaveToStorage() {
    const name = document.getElementById('save-patch-name').value.trim();
    if (!name) {
        showToast('Please enter a patch name', true);
        return;
    }
    
    const syncing = document.getElementById('save-syncing');
    const confirmBtn = document.getElementById('save-confirm-btn');
    
    syncing.classList.add('active');
    confirmBtn.disabled = true;
    
    const currentPatchName = getCurrentPatchNameFn();
    
    // If saving with a different name, force insert (it's a "Save As")
    const isNewName = name !== currentPatchName;
    const forceInsert = isNewName && currentPatchName !== 'Untitled Patch';
    
    // If force inserting, clear current ID first so we don't update old patch
    if (forceInsert) {
        storageService.clearCurrentPatchId();
    }
    
    const result = await storageService.savePatch(name, forceInsert);
    
    syncing.classList.remove('active');
    confirmBtn.disabled = false;
    
    if (result.success) {
        setCurrentPatchNameFn(name);
        recentPatchesService.addToRecentPatches(
            storageService.getCurrentPatchId() || '',
            name,
            Date.now(),
            getInstanceCountFn(),
            getConnectionCountFn()
        );
        invalidatePatchesCacheFn(); // Invalidate cache
        showToast(`Saved "${name}"`);
        closeSaveStorageModal();
        
        // Emit patch saved event
        eventBus.emit(EVENTS.PATCH_SAVED, { patchName: name });
    } else {
        const errorMsg = result.error ? `Save failed: ${result.error}` : 'Failed to save patch';
        showToast(errorMsg, true);
    }
}

// ============= OPEN STORAGE MODAL =============

/**
 * Open from storage (opens the Open modal in "open" mode)
 */
function openFromStorage() {
    closeFileMenu();
    showOpenStorageModal('open');
}

/**
 * Import from storage (opens the Open modal in "import" mode)
 */
function importFromStorage() {
    closeFileMenu();
    showOpenStorageModal('import');
}

/**
 * Show the Open Storage modal
 * @param {string} mode - 'open' or 'import'
 */
function showOpenStorageModal(mode) {
    const modal = document.getElementById(MODAL_IDS.OPEN_STORAGE);
    const title = document.getElementById('open-storage-title');
    const modeInput = document.getElementById('open-storage-mode');
    const searchInput = document.getElementById('open-search-input');
    
    modeInput.value = mode;
    title.textContent = mode === 'import' ? '📥 Import Patch' : '📂 Open Patch';
    
    // Clear search field
    if (searchInput) {
        searchInput.value = '';
        openSearchFilter = '';
    }
    
    // Invalidate caches BEFORE fetching to ensure fresh data from cloud
    invalidatePatchesCacheFn();
    
    // Note: storageService.invalidateCloudCache() is a no-op in UnifiedStorageService
    storageService.invalidateCloudCache();
    
    // Render appropriate patch list (will fetch fresh from cloud)
    renderOpenPatchList();
    
    modal.classList.add('active');
}

/**
 * Filter patch list in open modal
 * @param {string} query - Search query
 */
function filterOpenPatchList(query) {
    openSearchFilter = query.trim().toLowerCase();
    renderOpenPatchList();
}

/**
 * Render patches list for open modal
 */
async function renderOpenPatchList() {
    const list = document.getElementById('open-storage-list');
    const syncing = document.getElementById('open-syncing');
    
    syncing.classList.add('active');
    list.innerHTML = '';
    
    let patches = await storageService.listPatches();
    
    syncing.classList.remove('active');
    
    // Apply search filter
    if (openSearchFilter) {
        patches = patches.filter(p => p.name.toLowerCase().includes(openSearchFilter));
    }
    
    if (patches.length === 0) {
        if (openSearchFilter) {
            list.innerHTML = '<div class="storage-empty">No matching patches</div>';
        } else {
            list.innerHTML = '<div class="storage-empty">No saved patches</div>';
        }
    } else {
        list.innerHTML = patches.map(p => `
            <div class="storage-patch-item" onclick="openPatchFromList('${escapeHtml(p.id)}', '${escapeHtml(p.name)}')">
                <div class="storage-patch-info">
                    <div class="storage-patch-name">${escapeHtml(p.name)}</div>
                    <div class="storage-patch-meta">
                        <span>${p.deviceCount} devices</span>
                        <span>${p.connectionCount} connections</span>
                        <span>${formatTimestamp(p.timestamp)}</span>
                    </div>
                </div>
                <div class="storage-patch-actions">
                    <button onclick="event.stopPropagation(); deletePatchFromList('${escapeHtml(p.id)}', '${escapeHtml(p.name)}')" title="Delete">🗑️</button>
                </div>
            </div>
        `).join('');
    }
}

/**
 * Open a patch from the list
 * @param {string} patchId - Patch ID
 * @param {string} patchName - Patch name
 */
async function openPatchFromList(patchId, patchName) {
    const mode = document.getElementById('open-storage-mode').value;
    const merge = mode === 'import';
    const syncing = document.getElementById('open-syncing');
    
    syncing.classList.add('active');
    
    if (await loadAndApplyPatchFn(patchId, merge)) {
        showToast(merge ? `Imported "${patchName}"` : `Opened "${patchName}"`);
        closeOpenStorageModal();
        
        // Set patch name only when opening (not importing)
        if (!merge) {
            setCurrentPatchNameFn(patchName);
            recentPatchesService.addToRecentPatches(
                patchId,
                patchName,
                Date.now(),
                getInstanceCountFn(),
                getConnectionCountFn()
            );
        }
        
        autosaveFn(); // Immediate save after loading
        
        // Emit patch loaded event
        eventBus.emit(EVENTS.PATCH_LOADED, { patchName: patchName });
    } else {
        showToast('Failed to load patch', true);
    }
    
    syncing.classList.remove('active');
}

/**
 * Delete a patch from the list
 * @param {string} patchId - Patch ID
 * @param {string} patchName - Patch name
 */
async function deletePatchFromList(patchId, patchName) {
    const confirmed = await showConfirmModalFn({
        title: 'Delete Patch',
        message: `Delete "${patchName}"? This cannot be undone.`,
        confirmText: 'Delete',
        danger: true
    });
    if (!confirmed) return;
    
    if (await storageService.deletePatch(patchId)) {
        showToast(`Deleted "${patchName}"`);
        renderOpenPatchList();
    } else {
        showToast('Failed to delete patch', true);
    }
}

/**
 * Close the Open Storage modal
 */
function closeOpenStorageModal() {
    document.getElementById(MODAL_IDS.OPEN_STORAGE).classList.remove('active');
}
